/**
 * BuildingsPage - Lists and manages buildings/locations in the system.
 *
 * Features:
 * - Fetches real location data from the API
 * - SearchBar molecule for filtering by name (server-side)
 * - Dynamic filters that only appear when relevant:
 *	 - Company filter: shown when user is Super Admin/Developer OR has multiple companies
 *	 - City filter: shown when locations span more than one city
 *	 - State filter: shown when locations span more than one state
 * - Table with columns: Name, Address, Business Line, Sq Ft, Status
 * - "Add Building" button opens a creation modal (manager/admin/developer/super_admin roles)
 * - Responsive mobile card layout
 *
 * Accessible by: all authenticated users (protected by <ProtectedRoute>).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { Spinner } from '../../components/atoms/Spinner';
import { Alert, Badge, Button } from '../../components/atoms';
import { PageHeader } from '../../components/molecules/PageHeader';
import { SearchBar } from '../../components/molecules/SearchBar';
import { FilterBar } from '../../components/molecules/FilterBar';
import type { FilterConfig } from '../../components/molecules/FilterBar';
import { AddBuildingModal } from '../../components/organisms/AddBuildingModal';
import { DataTable } from '../../components/organisms/DataTable';
import { ResponsiveFilters } from '../../components/organisms/ResponsiveFilters';
import type { DataTableColumn } from '../../components/organisms/DataTable';
import { dedupeAndSortStrings } from '../../utils/dedupeAndSort';
import { LOCATION_MANAGE_ROLES } from '../../utils/constants';
import { qk } from '../../lib/queryKeys';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Location/building record shape from the API.
 *	Address fields are nullable — only the name is required at creation
 *	(the locations table allows null address/city/state/zip). */
interface Building {
	id: string | number;
	name: string;
	address: string | null;
	city: string | null;
	state: string | null;
	zip?: string | null;
	business_line?: string;
	cleanable_sqft?: number;
	status?: string;
	is_active?: boolean;
	/** Companies linked to this location (from eager-loaded relationship) */
	companies?: { company_id: number; name: string }[];
}

/** Company option for the company filter and company assignment dropdowns */
interface CompanyOption {
	id: string | number;
	name: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/** The status pill for a building, as the shared Badge atom.
 *	'completed' is the atom's green; 'default' its neutral grey. Both layouts
 *	(table cell and phone card) call this so the two can't drift apart. */
const renderStatusBadge = (status: string): React.ReactNode => (
	<Badge variant={status === 'active' || status === 'true' ? 'completed' : 'default'} className="capitalize whitespace-nowrap">{status}</Badge>
);

/** A building's status, whichever of the two shapes the API sent it in. */
const buildingStatus = (b: Building): string => b.status ?? (b.is_active !== false ? 'active' : 'inactive');

/* ============================================
 * COMPONENT
 * ============================================ */

const BuildingsPage: React.FC = () => {
	const { hasAnyRole } = useAuth();

	/* --- Filter state --- */
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedCompanyId, setSelectedCompanyId] = useState('');
	const [selectedCity, setSelectedCity] = useState('');
	const [selectedState, setSelectedState] = useState('');

	/* --- Add Building modal state (the form itself lives in the shared
		   AddBuildingModal organism, which the job forms open too) --- */
	const [modalOpen, setModalOpen] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	/* Determine if the user should see the company filter:
	   Super Admins and Developers always see it; others see it if they have 2+ companies */
	const isSuperUser = hasAnyRole(['super_admin', 'developer']);
	const canCreate = hasAnyRole([...LOCATION_MANAGE_ROLES]);

	/* ============================================
	 * DATA FETCHING
	 * ============================================ */

	/* Buildings list — keyed by the server-side params (search + company).
	 * Changing either filter re-keys the query and refetches automatically. */
	const buildingsParams = useMemo(() => {
		/* per_page=999 avoids pagination truncating the listing */
		const params: Record<string, string> = { per_page: '999' };
		if (searchQuery.trim()) params.search = searchQuery.trim();
		if (selectedCompanyId) params.company_id = selectedCompanyId;
		return params;
	}, [searchQuery, selectedCompanyId]);

	const buildingsQuery = useQuery({
		queryKey: qk.locations.list(buildingsParams),
		queryFn: async () => {
			try {
				const res = await api.get<any>('/locations', buildingsParams);
				const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
				return data as Building[];
			} catch (err: any) {
				throw new Error(err?.data?.message || 'Failed to load buildings.');
			}
		},
	});

	const buildings: Building[] = useMemo(() => buildingsQuery.data ?? [], [buildingsQuery.data]);
	const isLoading = buildingsQuery.isLoading;
	const error = buildingsQuery.error ? (buildingsQuery.error as Error).message : null;

	/** Fetch companies for the company filter dropdown and company assignment */
	const companiesQuery = useQuery({
		queryKey: qk.companies.lists(),
		queryFn: async () => {
			const res = await api.get<any>('/companies');
			const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
			return data.map((c: any) => ({ id: c.company_id || c.id, name: c.name })) as CompanyOption[];
		},
	});
	const companies: CompanyOption[] = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);

	/* Auto-dismiss success message after 3 seconds */
	useEffect(() => {
		if (!successMessage) return;
		const timer = setTimeout(() => setSuccessMessage(null), 3000);
		return () => clearTimeout(timer);
	}, [successMessage]);

	/* ============================================
	 * DERIVED FILTER OPTIONS
	 * Compute unique cities and states from the loaded buildings.
	 * Filters only appear when there are 2+ distinct values.
	 * ============================================ */

	/** Unique sorted cities from all loaded buildings — deduped and sorted A-Z */
	const uniqueCities = useMemo(() => {
		return dedupeAndSortStrings(buildings.map((b) => b.city));
	}, [buildings]);

	/** Unique sorted states from all loaded buildings — deduped and sorted A-Z */
	const uniqueStates = useMemo(() => {
		return dedupeAndSortStrings(buildings.map((b) => b.state));
	}, [buildings]);

	/** Company filter options — deduped by name (case-insensitive) and sorted A-Z.
		Prevents duplicate company names from appearing in the dropdown. */
	const uniqueCompanyOptions = useMemo(() => {
		const seen = new Set<string>();
		return companies
			.filter((c) => {
				const key = c.name.trim().toLowerCase();
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			})
			.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
	}, [companies]);

	/* If a re-fetch (e.g. changing the Company filter) collapses the dataset so
	 * the selected city/state is no longer present, clear it — otherwise the
	 * filter stays applied while its <select> is unmounted, silently filtering
	 * the list to zero rows with no in-place way to undo it. */
	useEffect(() => {
		if (selectedCity && !uniqueCities.includes(selectedCity)) setSelectedCity('');
	}, [uniqueCities, selectedCity]);
	useEffect(() => {
		if (selectedState && !uniqueStates.includes(selectedState)) setSelectedState('');
	}, [uniqueStates, selectedState]);

	/* ============================================
	 * FILTER BAR CONFIG
	 * FilterBar is driven by a config array plus a values record, so the
	 * "only show a filter that has something to choose between" rule is a
	 * filter() over the configs rather than three conditional <select>s.
	 * Labels are plural because the molecule renders "All {label}" as the
	 * empty option — "All Cities", not "All City".
	 * ============================================ */

	const filterConfigs: FilterConfig[] = useMemo(() => {
		const configs: FilterConfig[] = [];
		/* Companies: super_admin / developer always; anyone else once they
		   have more than one company to choose between. */
		if (isSuperUser || companies.length > 1) {
			configs.push({ key: 'company', label: 'Companies', type: 'select', options: uniqueCompanyOptions.map((c) => ({ value: String(c.id), label: c.name })) });
		}
		if (uniqueCities.length > 1) {
			configs.push({ key: 'city', label: 'Cities', type: 'select', options: uniqueCities.map((city) => ({ value: city, label: city })) });
		}
		if (uniqueStates.length > 1) {
			configs.push({ key: 'state', label: 'States', type: 'select', options: uniqueStates.map((st) => ({ value: st, label: st })) });
		}
		return configs;
	}, [isSuperUser, companies.length, uniqueCompanyOptions, uniqueCities, uniqueStates]);

	/** Current filter values, in the shape FilterBar reads and writes. */
	const filterValues = useMemo(
		() => ({ company: selectedCompanyId, city: selectedCity, state: selectedState }),
		[selectedCompanyId, selectedCity, selectedState],
	);

	/** Fans FilterBar's single values record back out to the three states.
	 *	Its "Clear" builds the next record from the filters it is rendering, so
	 *	a key can come back missing — falling back to '' is right there, since
	 *	a filter that isn't on screen shouldn't stay applied either. */
	const handleFilterChange = useCallback((next: Record<string, string>) => {
		setSelectedCompanyId(next.company ?? '');
		setSelectedCity(next.city ?? '');
		setSelectedState(next.state ?? '');
	}, []);

	/* ============================================
	 * CLIENT-SIDE FILTERING
	 * Company filter is sent to the API (server-side).
	 * City and state filters are applied client-side since
	 * we already have the full dataset loaded.
	 * ============================================ */

	const filteredBuildings = useMemo(() => {
		let result = buildings;
		if (selectedCity) {
			result = result.filter((b) => b.city === selectedCity);
		}
		if (selectedState) {
			result = result.filter((b) => b.state === selectedState);
		}
		return result;
	}, [buildings, selectedCity, selectedState]);

	/** Whether the list is narrowed at all — drives the "showing N of M" line.
	 *	Clearing is owned by the controls themselves: SearchBar's x for the
	 *	search, FilterBar's Clear for the dropdowns. */
	const hasActiveFilters = selectedCompanyId || selectedCity || selectedState || searchQuery;

	/** Dropdowns applied (search excluded — it stays on screen), for the Filters button. */
	const activeDropdownCount = [selectedCompanyId, selectedCity, selectedState].filter(Boolean).length;

	/* ============================================
	 * ADD BUILDING MODAL HANDLERS
	 * The form, validation, and the POST /locations call all live in the
	 * shared AddBuildingModal organism — this page only owns whether it's
	 * open and the success banner it leaves behind. The modal invalidates
	 * the locations cache itself, so the listing refetches on its own.
	 * ============================================ */

	/** Opens the Add Building modal */
	const openModal = useCallback(() => setModalOpen(true), []);

	/** Closes the modal */
	const closeModal = useCallback(() => setModalOpen(false), []);

	/** Success banner after the modal creates a building */
	const handleBuildingCreated = useCallback(() => {
		setSuccessMessage('Building created successfully.');
	}, []);

	/* ============================================
	 * TABLE COLUMN DEFINITIONS
	 * Each column specifies a header label and a render function for cell content.
	 * ============================================ */

	const columns: DataTableColumn<Building>[] = useMemo(() => [
		{ key: 'name', header: 'Name', render: (b) => <span className="font-medium text-brand-primary">{b.name}</span> },
		{ key: 'address', header: 'Address', render: (b) => <span className="text-brand-text-muted">{b.address || '--'}</span> },
		{ key: 'city', header: 'City', render: (b) => <span className="text-brand-text-muted">{b.city || '--'}</span> },
		{ key: 'state', header: 'State', render: (b) => <span className="text-brand-text-muted">{b.state || '--'}</span> },
		{
			key: 'customers',
			header: 'Customers',
			/* A building with no customer link never appears in the Building
			   dropdown on the work order / inspection create forms, so flag it
			   here rather than letting it quietly go unused. */
			render: (b) => (b.companies && b.companies.length > 0
				? <span className="text-brand-text-muted">{b.companies.map((c) => c.name).join(', ')}</span>
				: <Badge variant="pending">No customer</Badge>),
		},
		{ key: 'business_line', header: 'Business Line', render: (b) => <span className="text-brand-text-muted capitalize">{b.business_line?.replace(/_/g, ' ') || '--'}</span> },
		{ key: 'sqft', header: 'Sq Ft', render: (b) => <span className="text-brand-text-muted">{b.cleanable_sqft ? b.cleanable_sqft.toLocaleString() : '--'}</span> },
		{ key: 'status', header: 'Status', render: (b) => renderStatusBadge(buildingStatus(b)) },
	], []);

	/** Renders a single mobile card for a building — the phone half of
	 *	DataTable's two layouts. Every field the table shows appears here too,
	 *	the nullable ones skipped rather than rendered blank. */
	const renderMobileCard = useCallback((b: Building) => (
		<div className="buildings-page__card bg-white shadow rounded-lg p-16px border border-brand-border">
			{/* Name gives way, the status pill doesn't — min-w-0 on the text,
			    shrink-0 on the badge, or a long building name pushes the pill
			    off the card. */}
			<div className="buildings-page__card-header flex items-start justify-between gap-8px mb-8px">
				<h3 className="text-14px font-sans font-semibold text-brand-primary line-clamp-1 min-w-0">{b.name}</h3>
				<span className="shrink-0">{renderStatusBadge(buildingStatus(b))}</span>
			</div>
			{/* Address lines are nullable — skip empty rows instead of rendering
				a blank line or a dangling ", " for name-only buildings */}
			{b.address && <p className="text-12px font-sans text-brand-text-muted mb-4px break-words">{b.address}</p>}
			{(b.city || b.state) && (
				<p className="text-12px font-sans text-brand-text-muted mb-4px">{[b.city, b.state].filter(Boolean).join(', ')}</p>
			)}
			{/* Same unassigned-customer warning as the table's Customers column */}
			{b.companies && b.companies.length > 0 ? (
				<p className="text-12px font-sans text-brand-text-muted mb-4px break-words">{b.companies.map((c) => c.name).join(', ')}</p>
			) : (
				<p className="mb-4px"><Badge variant="pending">No customer</Badge></p>
			)}
			{b.business_line && <p className="text-12px font-sans text-brand-text-muted capitalize mb-4px">{b.business_line.replace(/_/g, ' ')}</p>}
			{b.cleanable_sqft && <p className="text-12px font-sans text-brand-text-muted">{b.cleanable_sqft.toLocaleString()} sq ft</p>}
		</div>
	), []);

	/* ============================================
	 * RENDER
	 * ============================================ */

	return (
		<PageContainer block="buildings-page" dataRef="page-buildings-page">
			{/* --- Page header (PageHeader molecule) --- */}
			<PageHeader
				title="Buildings"
				subtitle="Manage service locations and project sites."
				buttonLabel={canCreate ? 'Add Building' : undefined}
				onButtonClick={canCreate ? openModal : undefined}
			/>

			{/* --- Success message banner (auto-dismisses after 3s) --- */}
			{successMessage && (
				<Alert variant="success" className="mb-16px" onDismiss={() => setSuccessMessage(null)}>
					{successMessage}
				</Alert>
			)}

			{/* --- Toolbar: search + filters ---
				Mirrors the Admin page's toolbar so the two list surfaces behave
				the same. Both controls are the shared molecules: SearchBar
				brings its own clear button and 44px target, FilterBar brings
				the option dedupe, the labels, the wrap, and the "Clear" that
				resets every dropdown at once. */}
			<div className="buildings-page__toolbar flex flex-col tablet:flex-row tablet:flex-wrap tablet:items-end gap-12px w-full min-w-0 mb-16px">
				<SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search buildings by name..." className="buildings-page__search w-full tablet:w-auto tablet:min-w-[240px]" />

				{/* Dropdowns are built from the loaded data, so each one only
				    appears once there is something to choose between. */}
				{/* Below desktop they fold into a Filters button + pop-up so the
				    list keeps the screen; FilterBar's own Clear resets them, so
				    the pop-up doesn't add a second Reset. */}
				{filterConfigs.length > 0 && (
					<ResponsiveFilters title="Filter buildings" activeCount={activeDropdownCount} className="buildings-page__filters min-w-0" dataRef="page-buildings-filters">
						<FilterBar filters={filterConfigs} values={filterValues} onChange={handleFilterChange} />
					</ResponsiveFilters>
				)}
			</div>

			{/* --- Loading state --- */}
			{isLoading && (
				<div className="buildings-page__loading flex items-center justify-center py-48px">
					<Spinner size="lg" label="Loading buildings" />
				</div>
			)}

			{/* --- Error state — the shared banner, with the retry in its
				action slot rather than a hand-rolled link underneath. --- */}
			{error && !isLoading && (
				<Alert
					variant="error"
					icon
					className="buildings-page__error mb-16px"
					action={<Button variant="secondary" size="sm" onClick={() => buildingsQuery.refetch()}>Try Again</Button>}
				>
					{error}
				</Alert>
			)}

			{/* --- Results count --- */}
			{!isLoading && !error && filteredBuildings.length > 0 && hasActiveFilters && (
				<p className="buildings-page__results-count text-12px text-brand-text-muted mb-8px">Showing {filteredBuildings.length} of {buildings.length} buildings</p>
			)}

			{/* --- Buildings DataTable (organism) — renders desktop table + mobile cards --- */}
			{!isLoading && !error && (
				<DataTable<Building>
					columns={columns}
					data={filteredBuildings}
					keyExtractor={(b) => String(b.id)}
					renderMobileCard={renderMobileCard}
					emptyMessage={
						buildings.length === 0
							? (searchQuery ? 'No buildings found. Try a different search.' : canCreate ? 'Add your first building to get started.' : 'No buildings available.')
							: 'No buildings match the selected filters.'
					}
				/>
			)}

			{/* === ADD BUILDING MODAL ===
				Shared organism — the same modal the work order / inspection
				create form opens from its Building dropdown. */}
			<AddBuildingModal isOpen={modalOpen} onClose={closeModal} onCreated={handleBuildingCreated} />

		</PageContainer>
	);
};

export default BuildingsPage;
